// src/seo/buildSeoMetaInfo.js

const DEFAULT_SITE_ORIGIN = "https://www.hellobb.net";

/**
 * Normaliza una URL:
 * - si viene relativa, le pone el origin
 * - elimina slash final (excepto si es solo "/")
 */
function normalizeUrl(url, origin = DEFAULT_SITE_ORIGIN) {
  if (!url) return "";
  const abs = url.startsWith("http") 
    ? url 
    : `${origin}${url.startsWith("/") ? "" : "/"}${url}`;
  return abs.replace(/\/$/, "");
}

// "de-DE" -> "de", "pt_BR" -> "pt", "es" -> "es", "x-default" -> "x-default"
function normalizeHrefLang(hreflang) {
  if (!hreflang) return "es";
  if (hreflang === "x-default") return "x-default";
  return String(hreflang).split(/[-_]/)[0].toLowerCase();
}

/**
 * Convierte un locale interno (es, de-DE, en-GB...) a un html lang compacto (es, de, en...)
 * Si quieres mantener html lang igual que el locale, puedes no pasar htmlLangMap y usar appLang tal cual.
 */
function toHtmlLang(appLang, htmlLangMap = null, fallback = "es") {
  if (!appLang) return fallback;
  if (htmlLangMap && htmlLangMap[appLang]) return htmlLangMap[appLang];

  // "de-DE" -> "de", "pt_BR" -> "pt"
  const base = String(appLang).split(/[-_]/)[0].toLowerCase();
  return base || fallback;
}

function buildAlternates(alts, origin) {
  const out = [];
  const seen = new Set();

  for (const [key, href] of Object.entries(alts || {})) {
    const hreflang = normalizeHrefLang(key);
    if (seen.has(hreflang)) continue; // evita duplicados (en-GB + en-US -> en)
    seen.add(hreflang);

    out.push({
      rel: "alternate",
      hreflang,
      href: normalizeUrl(href, origin),
    });
  }

  // Asegura x-default si no está (opcional pero recomendable)
  if (!seen.has("x-default")) {
    // intenta usar la versión española como fallback, si existe
    const fallbackHref = alts?.es || Object.values(alts || {})[0];
    if (fallbackHref) {
      out.push({
        rel: "alternate",
        hreflang: "x-default",
        href: normalizeUrl(fallbackHref, origin),
      });
    }
  }

  return out;
}

/**
 * Construye metaInfo de vue-meta para SEO internacional.
 *
 * @param {Object} params
 * @param {Object} params.vm - this del componente Vue
 * @param {Object} params.alts - mapa hreflang -> url (absoluta o relativa)
 * @param {String} params.title - título ya traducido (sin el " | HelloBB" si no quieres)
 * @param {String} params.description - description ya traducida
 * @param {String} [params.brandSuffix=""]
 * @param {String} [params.origin="https://www.hellobb.net"]
 * @param {String} [params.appLang] - locale interno (vm.lang, vm.$i18n.locale, etc.)
 * @param {Object} [params.htmlLangMap] - mapa locale interno -> html lang
 * @param {String} [params.canonicalUrl] - si no lo pasas, se calcula con origin + route.path
 */
export function buildSeoMetaInfo({
  vm,
  alts,
  title,
  description,
  brandSuffix = "",
  origin = DEFAULT_SITE_ORIGIN,
  appLang,
  htmlLangMap,
  canonicalUrl,
}) {
  // canonical = URL actual por defecto
  const computedCanonical = canonicalUrl
    ? normalizeUrl(canonicalUrl, origin)
    : normalizeUrl(`${origin}${vm.$route.path}`, origin);

  // alternates desde mapa (incluye x-default si lo has puesto en alts)
  const alternates = buildAlternates(alts, origin);
  return {
    title: `${title}${brandSuffix}`,
    htmlAttrs: {
      lang: toHtmlLang(appLang, htmlLangMap, "es"),
    },
    meta: [
      {
        name: "description",
        content: description,
      },
    ],
    link: [
      { rel: "canonical", href: computedCanonical },
      ...alternates,
    ],
    __dangerouslyDisableSanitizers: ["title", "meta"],
  };
}
